import { __awaiter } from 'tslib';
import 'long';
import '../proto/index.js';
import { ErrorType, IMEvent } from '../core/consts.js';
import { Logger } from '../core/metrics/logger.js';
import { MonitorItem, Monitor } from '../core/metrics/monitor.js';
import { ServiceIdentity } from '../core/service/RuntimeContext.js';
import '../core/network/NetworkItem.js';
import 'json-long';
import 'moize';
import '../core/model/Message.js';
import 'js-md5';
import '../core/utils/lz4.js';
import '../core/utils/serializer.js';
import '../adapter/manager/AdapterManager.js';
import '../adapter/manager/types.js';
import '../core/utils/ticker.js';
import '../adapter/abstract/IMNetApi.js';
import '../core/options.js';
import '../core/utils/uuid.js';
import '../core/service/ConversationManager.js';
import '../core/service/MessageManager.js';
import '@byted/uc-secure-sdk';
import '../core/plugin/DbProxy.js';
import { TrackerItem } from '../core/metrics/trackerTypes.js';
import { BasePlugin } from '../core/plugin/BasePlugin.js';
import '../core/model/CommandMessage.js';
import { SlardarWebMonitor } from './monitor/SlardarWebMonitor.js';
import { SlardarSdkMonitor } from './monitor/SlardarSdkMonitor.js';

class MetricsPlugin extends BasePlugin {
    constructor() {
        super(...arguments);
        this.ignoreMethods = [
            // 非 Promise 方法不需要
            'getStrangerTotalUnread',
            'getLocalStrangerConversation',
            'checkRtcRequirements',
            'parseContent',
            'unreadCountReport',
            'getMessageReferencedInfo',
            'initAdapter',
            'getConversationParticipants',
            'broadcastGetConversation',
            // 消息类
            'createMessage',
            'createTextMessage',
            'createFileMessage',
            'sendMessage',
            'getMessageReadReceipt',
            // 会话类
            'createConversation',
            'getConversationOnline',
            'getConversationListOnline',
            // 陌生人的一些接口
            'getStrangerPreview',
            // emoji 相关
            'emojify',
            'randomEmoji',
            'getAllEmoji',
            // BytedIM private
            'constructor',
            'getUserCursor',
            'setUserCursor',
            'init',
            'getConversation',
            'getConversationList',
            'dispose',
            'intervalFunc',
            'receivePacket',
            'messageFilter',
            'sendMessageObject',
            'tickerUpdate',
            'prepareHistoryForInbox',
            'prepareToken',
            'clearCache',
            'resolve',
            'getContext',
            'processInitMessage',
            'processInitConversation',
            'patchMessage',
            'clientAck'
        ];
    }
    beforeHook(name, params) {
        if (this.ctx.option.aspectBefore) {
            return this.ctx.option.aspectBefore({
                name,
                params
            });
        }
    }
    afterHook(name, params, startTime, result) {
        const duration = this.resolve(ServiceIdentity.Monitor).emitDuration(MonitorItem.BizApiInvoke + MonitorItem.SuccessSuffix, startTime, {
            name
        });
        if (this.ctx.option.aspectAfter) {
            this.ctx.option.aspectAfter({
                name,
                params,
                duration,
                result
            });
        }
    }
    errorHook(name, params, e, startTime) {
        const duration = this.resolve(ServiceIdentity.Monitor).emitDuration(MonitorItem.BizApiInvoke + MonitorItem.ErrorSuffix, startTime, {
            name,
            error: e.type ? `${e.type}:${ErrorType[e.type]}` : 'unknown'
        });
        if (this.ctx.option.aspectError) {
            const needThrow = this.ctx.option.aspectError({
                name,
                params,
                duration,
                error: e
            });
            if (needThrow !== undefined) {
                return Boolean(needThrow);
            }
            else {
                return true;
            }
        }
        return true;
    }
    wrapMetricsAsync(name, func) {
        let startTime = 0;
        return (params) => __awaiter(this, void 0, void 0, function* () {
            try {
                const allowInvoke = this.beforeHook(name, params);
                if (allowInvoke === false) {
                    return;
                }
                startTime = Monitor.performanceNow();
                const result = yield func.call(this.instance, params);
                this.afterHook(name, params, startTime, result);
                return result;
            }
            catch (e) {
                const needThrow = this.errorHook(name, params, e, startTime);
                if (needThrow) {
                    throw e;
                }
            }
        });
    }
    getFuncs() {
        let props = [];
        const { instance } = this;
        const instanceMethod = Object.getOwnPropertyNames(instance);
        props = props.concat(instanceMethod);
        const prototypeInstance = Object.getPrototypeOf(instance);
        const prototypeMethod = Object.getOwnPropertyNames(prototypeInstance);
        props = props.concat(prototypeMethod);
        const prototypeTopMethod = Object.getOwnPropertyNames(Object.getPrototypeOf(prototypeInstance));
        props = props.concat(prototypeTopMethod);
        return props.filter(k => typeof this.instance[k] === 'function' &&
            !this.ignoreMethods.includes(k) &&
            !k.startsWith('__internal'));
    }
    install() {
        this.register(ServiceIdentity.MetricsPlugin, this);
        if (this.ctx.option.timeCalibration === undefined) {
            this.ctx.option.timeCalibration = true;
        }
        // unify monitor
        this.ctx.option.monitor = this.unifyMonitorConfig();
        // inject metrics monitor
        const metricsMonitor = this.initMetricsMonitor();
        if (metricsMonitor !== undefined) {
            Logger.ctxDebug(this.ctx, 'install metrics monitor:', metricsMonitor);
            this.ctx.option.monitor.push(metricsMonitor);
        }
        // creating metrics proxy
        const instance = this.instance;
        const funcs = this.getFuncs();
        for (const methodName of funcs) {
            const valueMethod = instance[methodName];
            Logger.ctxDebug(this.ctx, `wrapping metrics for ${methodName}`);
            instance[methodName] = this.wrapMetricsAsync(methodName, valueMethod);
        }
        this.subscribeTrackerEvent();
    }
    receivePacket(packet) {
        return __awaiter(this, void 0, void 0, function* () {
            return;
        });
    }
    // TODO: unify the report mode (built-in core or plugin)
    subscribeTrackerEvent() {
        this.resolve(ServiceIdentity.EventBus).subscribe(IMEvent.Error, err => {
            this.resolve(ServiceIdentity.Monitor).emitTracker(TrackerItem.Exception, {
                error_stack: err.stack
            });
        });
        this.resolve(ServiceIdentity.EventBus).subscribe(IMEvent.InitFinish, result => {
            const convs = this.instance.getConversationList();
            const msgCount = convs.map(c => c.getMessageList().length).reduce((all, current) => all + current, 0);
            this.resolve(ServiceIdentity.Monitor).emitTracker(TrackerItem.InitHandler, {
                conv_count: convs.length,
                total_msg_count: msgCount,
                is_success: '1'
            });
        });
    }
    unifyMonitorConfig() {
        const rawMonitor = this.ctx.option.monitor;
        if (Array.isArray(rawMonitor)) {
            return rawMonitor;
        }
        else if (rawMonitor !== undefined) {
            this.ctx.option.monitor = [rawMonitor];
            return this.ctx.option.monitor;
        }
        this.ctx.option.monitor = [];
        return this.ctx.option.monitor;
    }
    initMetricsMonitor() {
        if (typeof this.ctx.option.metrics === 'function') {
            // using slardar/sdk or slardar/web
            const slardarInstance = this.ctx.option.metrics;
            // using @slardar/web
            if (typeof slardarInstance.sendEvent === 'function' && typeof slardarInstance.sendLog === 'function') {
                Logger.ctxDebug(this.ctx, 'use provided @slardar/web instance', this.ctx.option.metrics);
                return new SlardarWebMonitor(this.ctx.option.metrics);
            }
            else {
                // using @slardar/sdk
                Logger.ctxDebug(this.ctx, 'use provided @slardar/sdk instance', this.ctx.option.metrics);
                return new SlardarSdkMonitor(this.ctx.option.metrics);
            }
        }
    }
}

export { MetricsPlugin };
//# sourceMappingURL=MetricsPlugin.js.map
